import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production'
    ? '/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const TestRunModal = ({ isOpen, onClose, projectName, projectTitle, onRunStarted }) => {
    const [mode, setMode] = useState('test_case_ids');
    const [testCaseIds, setTestCaseIds] = useState('149803, 150217, 150218');
    const [testRunId, setTestRunId] = useState('6662');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (mode === 'test_case_ids' && !testCaseIds.trim()) { setError('Please enter at least one test case ID'); return; }
        if (mode === 'test_run' && !testRunId.trim()) { setError('Please enter a test run ID'); return; }

        setLoading(true);
        setError(null);
        const token = localStorage.getItem('authToken');
        const body = {};
        if (mode === 'test_case_ids') {
            body.test_case_ids = testCaseIds.split(',').map(id => id.trim()).filter(id => id.length > 0);
        } else {
            body.test_run_id = testRunId.trim();
        }

        try {
            const response = await axios.post(`${API_URL}/digital-qa/test/${projectName}/start`, body, { headers: { Authorization: `Bearer ${token}` } });
            if (response.data.success) {
                if (onRunStarted) onRunStarted(response.data);
                setTestCaseIds(''); setTestRunId(''); onClose();
            } else { setError(response.data.message || 'Failed to start test run'); }
        } catch (err) { setError(err.response?.data?.message || 'Failed to start test run.'); }
        finally { setLoading(false); }
    };

    const handleClose = () => { setTestCaseIds(''); setTestRunId(''); setError(null); onClose(); };

    if (!isOpen) return null;

    const inputClass = "bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500";
    const labelClass = "block mb-2 text-sm font-medium text-gray-900 dark:text-white";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/50 dark:bg-gray-900/80" onClick={handleClose}>
            <div className="relative w-full max-w-lg p-4" onClick={(e) => e.stopPropagation()}>
                <div className="relative bg-white rounded-lg shadow dark:bg-gray-800">
                    <div className="flex items-center justify-between p-4 border-b dark:border-gray-600">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Start New Test Run</h3>
                        <button onClick={handleClose} className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" /></svg>
                        </button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className={labelClass}>Project</label>
                                <input type="text" value={projectTitle || projectName} disabled className={`${inputClass} bg-gray-100 dark:bg-gray-600 cursor-not-allowed`} />
                            </div>
                            <div>
                                <label className={labelClass}>Platform</label>
                                <input type="text" value="Web" disabled className={`${inputClass} bg-gray-100 dark:bg-gray-600 cursor-not-allowed`} />
                            </div>
                            <div>
                                <label className={labelClass}>Input Type</label>
                                <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                                    <button type="button" onClick={() => { setMode('test_case_ids'); setError(null); }}
                                        className={`flex-1 py-2 text-sm font-medium ${mode === 'test_case_ids' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300'}`}>
                                        Test Case IDs
                                    </button>
                                    <button type="button" onClick={() => { setMode('test_run'); setError(null); }}
                                        className={`flex-1 py-2 text-sm font-medium ${mode === 'test_run' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300'}`}>
                                        Test Run
                                    </button>
                                </div>
                            </div>
                            {mode === 'test_case_ids' ? (
                                <div>
                                    <label className={labelClass}>Test Case IDs</label>
                                    <input type="text" placeholder="e.g. 147695, 147696, 147697" value={testCaseIds}
                                        onChange={(e) => setTestCaseIds(e.target.value)} className={inputClass} autoFocus />
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Enter comma-separated test case IDs</p>
                                </div>
                            ) : (
                                <div>
                                    <label className={labelClass}>Test Run ID</label>
                                    <input type="text" placeholder="e.g. 12345" value={testRunId}
                                        onChange={(e) => setTestRunId(e.target.value)} className={inputClass} autoFocus />
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Enter a single test run ID</p>
                                </div>
                            )}
                            {error && <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-700 dark:text-red-400">{error}</div>}
                        </div>
                        <div className="flex items-center p-6 space-x-2 border-t border-gray-200 dark:border-gray-600">
                            <button type="submit" disabled={loading}
                                className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 disabled:opacity-50">
                                {loading ? 'Starting...' : 'Start Test Run'}
                            </button>
                            <button type="button" onClick={handleClose}
                                className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TestRunModal;
